import React, { useMemo } from 'react'
import { formatStakingBadgeNumber } from '@components/ReputationBadges/TownsReputationBadge'
import { StakingBadge } from '@components/StakingBadge/StakingBadge'
import { StakingBadgeConfig, useStakingBadge } from '@components/StakingBadge/useStakingBadges'
import { Box, Icon, MotionBox, Paragraph } from '@ui'
import { AnimatePresence } from 'framer-motion'

type Props = {
    value: number
}

// Create sorted badges array for consistent ordering
const sortedBadges = (['ruby', 'sapphire', 'diamond'] as const)
    .map((badge) => ({
        key: badge,
        name: StakingBadgeConfig[badge].name,
        threshold: StakingBadgeConfig[badge].threshold,
    }))
    .sort((a, b) => a.threshold - b.threshold)

export const StakingProgressViz = (props: Props) => {
    const { value: _value } = props
    const value = isNaN(_value) ? 0 : _value

    const currentBadge = useStakingBadge(value)

    const rangeRatio = 1 / (sortedBadges.length - 1)

    const percent = useMemo(() => {
        if (value <= sortedBadges[0].threshold) {
            return 0
        }

        for (let i = 0; i < sortedBadges.length; i++) {
            if (value <= sortedBadges[i].threshold) {
                const lowerThreshold = i === 0 ? 0 : sortedBadges[i - 1].threshold
                const range = sortedBadges[i].threshold - lowerThreshold
                const currentRange = (value - lowerThreshold) / range
                const adjustedValue = (i - 1) * rangeRatio + currentRange * rangeRatio
                return Math.max(0, Math.min(1, adjustedValue)) * 100
            }
        }

        return 100
    }, [rangeRatio, value])

    const gradient =
        currentBadge.type === 'neutral'
            ? { start: '#fff', end: '#fff' }
            : StakingBadgeConfig[currentBadge.type].colors

    return (
        <Box paddingX="x4" position="relative">
            <Box horizontal position="relative" justifyContent="spaceBetween" width="100%">
                <ProgressBar percent={percent} gradient={gradient} />
                {sortedBadges.map((badge) => (
                    <Box centerContent key={badge.key} insetX="xxs" position="relative" gap="sm">
                        <Box width="x4" height="x4" insetX="xxs" position="relative">
                            <StakingBadge
                                stroke
                                showIcon
                                type="base"
                                style={{
                                    position: `absolute`,
                                }}
                            />
                            <StakingBadge
                                stroke
                                type={badge.key}
                                style={{
                                    position: 'absolute',
                                    transition: 'opacity 400ms ease-in-out',
                                    opacity:
                                        currentBadge.type === 'neutral' ||
                                        currentBadge.type === 'base' ||
                                        badge.key === currentBadge.type
                                            ? 1
                                            : 0,
                                }}
                            />
                            <AnimatePresence>
                                {badge.key === currentBadge.type && (
                                    <MotionBox
                                        position="absolute"
                                        top="none"
                                        right="none"
                                        background="positive"
                                        width="x2"
                                        height="x2"
                                        rounded="full"
                                        alignItems="center"
                                        justifyContent="center"
                                        initial={{ opacity: 0, scale: 0 }}
                                        animate={{ opacity: 1, scale: 0.9, x: 3, y: -2 }}
                                        transition={{ duration: 0.2, ease: 'easeInOut' }}
                                        border="inverted"
                                    >
                                        <Icon type="check" size="square_xxs" />
                                    </MotionBox>
                                )}
                            </AnimatePresence>
                        </Box>
                        <Box height="x4">
                            <Box
                                position="absolute"
                                style={{ transform: 'translateX(-50%)' }}
                                gap="sm"
                                color="gray2"
                            >
                                <Paragraph
                                    textAlign="center"
                                    whiteSpace="nowrap"
                                    fontWeight="medium"
                                    color={currentBadge.type === badge.key ? 'default' : 'gray2'}
                                >
                                    {badge.name}
                                </Paragraph>
                                <Paragraph textAlign="center" whiteSpace="nowrap" size="sm">
                                    {formatStakingBadgeNumber.format(badge.threshold)} $TOWNS
                                </Paragraph>
                            </Box>
                        </Box>
                    </Box>
                ))}
            </Box>
        </Box>
    )
}

const ProgressBar = (props: { percent: number; gradient: { start: string; end: string } }) => {
    const { percent, gradient } = props

    return (
        <Box position="absolute" height="x4" width="100%" justifyContent="center">
            <Box
                width="100%"
                height="x1"
                background="level3"
                borderRadius="md"
                position="relative"
                alignItems="center"
            >
                {/* progress bar */}
                <Box horizontal overflow="hidden" position="absolute" width="100%" height="100%">
                    <Box
                        position="absolute"
                        height="x1"
                        style={{
                            width: `${percent}%`,
                            transition: 'width 0.2s ease-in-out',
                            background: `linear-gradient(to right, ${gradient.start}, ${gradient.end})`,
                        }}
                        borderRadius="md"
                    />
                </Box>
            </Box>
        </Box>
    )
}
